/**
 * TextArea (UI-SPEC W13) — multi-line input with optional character
 * counter. Used by AssetEditForm for the asset description field.
 *
 * Counter renders `{n}/{max}` in muted text below the textarea when
 * `counter` is true. Counter uses Latin digits (req.md I18N-9.6).
 */
 
type ChangeHandler = (newValue: string) => void;

interface TextAreaProps {
  id: string;
  label: string;
  value: string;
  onChange: ChangeHandler;
  maxLength?: number;
  counter?: boolean;
  error?: string;
  helperText?: string;
  rows?: number;
  "data-testid"?: string;
}

export function TextArea({
  id,
  label,
  value,
  onChange,
  maxLength,
  counter = false,
  error,
  helperText,
  rows = 4,
  "data-testid": testId,
}: TextAreaProps) {
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-sm font-semibold text-on-surface">
        {label}
      </label>
      <textarea
        id={id}
        rows={rows}
        value={value}
        maxLength={maxLength}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className={`min-h-[96px] w-full bg-surface-raised border rounded-md ps-3 pe-3 pbs-2 pbe-2 text-base text-on-surface text-start resize-y focus-visible:ring-2 focus-visible:ring-primary focus-visible:border-transparent outline-none transition-colors duration-200 ${
          error ? "border-error" : "border-divider"
        }`}
      />
      {counter && typeof maxLength === "number" && (
        <div className="flex justify-end mbs-1">
          <span className="text-sm text-text-secondary" data-testid={`${testId ?? id}-counter`}>
            {value.length}/{maxLength}
          </span>
        </div>
      )}
      {helperText && <p className="text-sm text-text-secondary">{helperText}</p>}
      {error && <p className="text-sm text-error">{error}</p>}
    </div>
  );
}
